'use client';

import { useRouter } from 'next/navigation';
import React from 'react';

import { BackButton, MainButton, SecondaryButton } from '../../atomic-atoms';

import styles from './PageHeaders.module.scss';

export type PageFormHeaderProps = {
  title: string;
  /**
   * * The id of the `<form />` element this header's Save button should submit.
   * * Uses the native HTML `form` attribute so the button can live outside the form.
   */
  formID: string;
  isSaving?: boolean;
  /**
   * * Optional override for Back button behavior.
   */
  onBack?: () => void;
  /**
   * * Optional override for Cancel button behavior (defaults to `router.back()`).
   */
  onCancel?: () => void;
  cancelAriaLabel?: string;
  saveAriaLabel?: string;
  cancelText?: string;
  saveText?: string;
  controlsRight?: React.ReactNode;
};

export function PageFormHeader({
  title,
  formID,
  isSaving = false,
  onBack,
  onCancel,
  cancelAriaLabel = 'Cancel',
  saveAriaLabel = 'Save',
  cancelText = 'Cancel',
  saveText = 'Save',
  controlsRight,
}: PageFormHeaderProps) {
  const router = useRouter();

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <BackButton onClick={onBack} />
        <h1 className={styles.title}>{title}</h1>
      </div>

      <div className={styles.headerRight}>
        <SecondaryButton
          ariaLabel={cancelAriaLabel}
          onClick={onCancel ?? (() => router.back())}
        >
          {cancelText}
        </SecondaryButton>

        <MainButton
          type="submit"
          ariaLabel={saveAriaLabel}
          form={formID}
          disabled={isSaving}
          isLoading={isSaving}
        >
          {saveText}
        </MainButton>

        {controlsRight}
      </div>
    </header>
  );
}
